import React, { useCallback, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'

import styled from 'styled-components/native'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { AIDraft, AIDraftPromptType } from '@lyrahealth-inc/shared-app-logic/src/models/draft/AIDraft'
import { AIGeneratedTag } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/aiGeneratedTag/AIGeneratedTag'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { ConfirmationModal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/confirmationModal/ConfirmationModal'
import { TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'

import MessengerUtilityItemSmartSuggestionSection from './MessengerUtilityItemSmartSuggestionSection'
import { mixpanelEvents } from '../../../../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../../../../mixpanel/mixpanelTracking'
import { getAuthUserId } from '../../../../../../data/auth/authSelectors'
import { useAppDispatch } from '../../../../../../data/store'
import { setToastContent } from '../../../../../data/ltToastAutoActions'
import { useExpireAIDraft } from '../../../../data/hooks/useExpireAIDraft'
import { useGetAIDraftsBulkQuery } from '../../../../data/messagesApi'
import useNavigateToClientAssignment from '../../../../hooks/useNavigateToClientAssignment'
import MessengerUtilityItemContainer from '../../MessengerUtilityItemContainer'
import MessengerUtilityItemContentContainer from '../../MessengerUtilityItemContentContainer'
import MessengerUtilityItemEmptyState from '../../MessengerUtilityItemEmptyState'
import MessengerUtilityItemHeader from '../../MessengerUtilityItemHeader'
import useMapAIDraftsToUtilityItems from './hooks/useMapAIDraftsToUtilityItems'
import useNavigateToClientNote from '../../../../hooks/useNavigateToClientNote'

type MessengerUtilityItemSmartSuggestionsProps = {
  onConversationPressed: ({ clientId, externallySelected }: { clientId: string; externallySelected?: boolean }) => void
}

const Header = styled(MessengerUtilityItemHeader)(({ theme }) => ({
  gap: theme.spacing['8px'],
}))

const MessengerUtilityItemSmartSuggestions: React.FC<MessengerUtilityItemSmartSuggestionsProps> = ({
  onConversationPressed,
}) => {
  const { shouldShowPostSessionAIDrafts } = useFlags()
  const providerId = useSelector(getAuthUserId)
  const { data: aiIntakeDrafts, isFetching: isLoadingIntakeDrafts } = useGetAIDraftsBulkQuery(
    {
      promptType: AIDraftPromptType.INTAKE,
      statusOnly: false,
      providerId: providerId,
    },
    { refetchOnMountOrArgChange: true },
  )

  const { data: aiPostSessionDrafts, isFetching: isLoadingPostSessionDrafts } = useGetAIDraftsBulkQuery(
    {
      promptType: AIDraftPromptType.POST_SESSION,
      statusOnly: false,
      providerId: providerId,
    },
    { refetchOnMountOrArgChange: true, skip: !shouldShowPostSessionAIDrafts },
  )

  const [aiDraftToExpire, setAIDraftToExpire] = useState<AIDraft | null>(null)
  const [isExpiringDraft, setIsExpiringDraft] = useState(false)
  const dispatch = useAppDispatch()

  const navigateToAssignment = useNavigateToClientAssignment()
  const navigateToNote = useNavigateToClientNote()

  const expireAIDraft = useExpireAIDraft()
  const onDeleteDraft = useCallback(() => {
    if (!aiDraftToExpire) {
      return
    }
    setIsExpiringDraft(true)
    track({
      event: mixpanelEvents.DELETE_SMART_SUGGESTION_CONFIRM,
      properties: {
        gen_ai_submission_tracker_id: aiDraftToExpire.id,
        prompt_type: aiDraftToExpire.prompt_type,
      },
    })
    expireAIDraft(aiDraftToExpire.id)
      .unwrap()
      .then(() => {
        dispatch(
          setToastContent({
            text: 'Smart suggestion deleted',
            toastType: 'success',
            id: 'AIDraft-toast-delete-success',
          }),
        )
      })
      .catch(() => {
        dispatch(
          setToastContent({
            text: 'Failed to delete smart suggestion',
            toastType: 'error',
            id: 'AIDraft-toast-delete-draft-error',
          }),
        )
      })
      .finally(() => {
        setAIDraftToExpire(null)
        setIsExpiringDraft(false)
      })
  }, [aiDraftToExpire, dispatch, expireAIDraft])

  const intakeItems = useMapAIDraftsToUtilityItems(
    aiIntakeDrafts ?? [],
    onConversationPressed,
    navigateToAssignment,
    navigateToNote,
    setAIDraftToExpire,
  )
  const postSessionItems = useMapAIDraftsToUtilityItems(
    aiPostSessionDrafts ?? [],
    onConversationPressed,
    navigateToAssignment,
    navigateToNote,
    setAIDraftToExpire,
  )

  return (
    <MessengerUtilityItemContainer>
      <Header>
        <Title
          size={TitleSize.MEDIUM}
          text={<FormattedMessage defaultMessage='Smart suggestions' description='Header for smart suggestions page' />}
        />
        <AIGeneratedTag />
      </Header>
      {isLoadingIntakeDrafts || isLoadingPostSessionDrafts ? (
        <LoadingIndicator />
      ) : intakeItems.length === 0 && postSessionItems.length === 0 ? (
        <MessengerUtilityItemEmptyState
          text={
            shouldShowPostSessionAIDrafts ? (
              <FormattedMessage
                defaultMessage='Autogenerated summaries will appear here after each completed intake form and session.'
                description='Empty state for smart suggestions page'
              />
            ) : (
              <FormattedMessage
                defaultMessage='Autogenerated summaries will appear here after each completed intake form.'
                description='Empty state for smart suggestions page'
              />
            )
          }
        />
      ) : (
        <>
          {intakeItems.length > 0 && (
            <MessengerUtilityItemContentContainer maxHeight='50%'>
              <MessengerUtilityItemSmartSuggestionSection
                items={intakeItems}
                title={
                  <FormattedMessage
                    defaultMessage='Care intake summary'
                    description='Subheader for intake summary section'
                  />
                }
                tooltip={
                  <FormattedMessage
                    defaultMessage='Share recap of client’s intake form'
                    description='Tooltip for intake summary section'
                  />
                }
              />
            </MessengerUtilityItemContentContainer>
          )}
          {postSessionItems.length > 0 && (
            <MessengerUtilityItemContentContainer maxHeight='50%'>
              <MessengerUtilityItemSmartSuggestionSection
                items={postSessionItems}
                title={
                  <FormattedMessage
                    defaultMessage='Session summary'
                    description='Subheader for post-session summary section'
                  />
                }
                tooltip={
                  <FormattedMessage
                    defaultMessage='Share insights from session notes'
                    description='Tooltip for post-session summary section'
                  />
                }
              />
            </MessengerUtilityItemContentContainer>
          )}
        </>
      )}
      <ConfirmationModal
        visible={aiDraftToExpire !== null}
        onRequestClose={() => setAIDraftToExpire(null)}
        cancelButtonText='Cancel'
        confirmationButtonText='Confirm'
        onConfirmationButtonPress={onDeleteDraft}
        modalTitle='Delete smart suggestion'
        modalContents='Are you sure you want to delete this smart suggestion? This action cannot be undone.'
        confirmButtonLoading={isExpiringDraft}
      />
    </MessengerUtilityItemContainer>
  )
}

export default MessengerUtilityItemSmartSuggestions
